import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Trophy, Users, Plus, LogIn, Bomb, BarChart3, Gavel, Shield, Bell, CheckCircle2 } from 'lucide-react';
import CreateLeagueWizard from '@/components/league/CreateLeagueWizard';
import JoinLeaguePage from '@/components/league/JoinLeaguePage';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/gtag';
import sabotageLogo from '@/assets/sabotage-logo.png';

interface MLBBetaLandingProps {
  onLeagueReady: (leagueId: string) => void;
  // SAB-592: when the landing is opened from the shell (a user who already has a
  // league tapping Join/Create in the switcher), start on that view and let cancel
  // return to the shell instead of the landing home.
  initialView?: LandingView;
  onExit?: () => void;
}

type LandingView = 'home' | 'create' | 'join';

const MLBBetaLanding = ({ onLeagueReady, initialView = 'home', onExit }: MLBBetaLandingProps) => {
  const [view, setView] = useState<LandingView>(initialView);
  const cancelView = () => (onExit ? onExit() : setView('home'));
  const { user } = useAuth();
  const navigate = useNavigate();

  // NFL waitlist state
  const [nflEmail, setNflEmail] = useState('');
  const [nflStatus, setNflStatus] = useState<'idle' | 'submitting' | 'success' | 'duplicate' | 'error'>('idle');

  const handleNflSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nflEmail.trim()) return;
    setNflStatus('submitting');
    const { error } = await supabase.from('nfl_waitlist' as any).insert({ email: nflEmail.trim().toLowerCase(), source: 'mlbbeta', role: 'commissioner' } as any);
    if (error) {
      if (error.code === '23505') {
        setNflStatus('duplicate');
      } else {
        setNflStatus('error');
      }
    } else {
      setNflStatus('success');
      trackEvent('nfl_waitlist_signup', { source: 'mlbbeta' });
    }
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6 px-4">
        <div className="text-center space-y-3">
          <img src={sabotageLogo} alt="Sabotage Fantasy" className="max-h-20 w-auto mx-auto" />
          <h1 className="text-3xl font-bold font-heading text-foreground">Sabotage Fantasy</h1>
          <p className="text-muted-foreground max-w-md">
            The anti-fantasy league where the worst players win. Draft the biggest busts, rack up trash points, and sabotage your opponents.
          </p>
        </div>
        <Button size="lg" onClick={() => navigate('/auth')}>
          <LogIn className="w-4 h-4 mr-2" />
          Sign In to Get Started
        </Button>
      </div>
    );
  }

  if (view === 'create') {
    return (
      <CreateLeagueWizard
        onComplete={(leagueId) => {
          if (leagueId) onLeagueReady(leagueId);
        }}
        onCancel={cancelView}
      />
    );
  }

  if (view === 'join') {
    return (
      <JoinLeaguePage
        onJoin={(leagueId) => onLeagueReady(leagueId)}
        onCancel={cancelView}
      />
    );
  }

  return (
    <div className="space-y-8 pb-20 md:pb-0">
      {/* Hero */}
      <div className="text-center space-y-3 pt-4">
        <img src={sabotageLogo} alt="Sabotage Fantasy" className="max-h-20 w-auto mx-auto" />
        <h1 className="text-3xl font-bold font-heading text-foreground">Welcome to Sabotage Fantasy</h1>
        <p className="text-muted-foreground max-w-lg mx-auto">
          Create a league and draft the worst MLB players. The more your players strike out, give up runs, and commit errors, the more you score.
        </p>
      </div>

      {/* NFL Waitlist Section */}
      <div className="max-w-2xl mx-auto">
        <Card className="border-orange-500/40 bg-orange-500/5">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <Badge className="bg-orange-500 text-white hover:bg-orange-600 text-xs font-bold px-2 py-0.5">NFL</Badge>
              <CardTitle className="text-xl font-heading">NFL is Coming</CardTitle>
            </div>
            <CardDescription className="text-sm pt-1">
              Be the first to create a league when NFL goes live in August 2026. Sign up to get notified when commissioner signups open in June.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {nflStatus === 'success' ? (
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <CheckCircle2 className="w-4 h-4" />
                You're on the list. We'll notify you when NFL leagues open.
              </div>
            ) : nflStatus === 'duplicate' ? (
              <div className="flex items-center gap-2 text-orange-400 text-sm">
                <CheckCircle2 className="w-4 h-4" />
                You're already signed up. We'll be in touch.
              </div>
            ) : (
              <form onSubmit={handleNflSignup} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={nflEmail}
                  onChange={(e) => setNflEmail(e.target.value)}
                  required
                  className="flex-1 border-orange-500/30 focus-visible:ring-orange-500/50"
                />
                <Button
                  type="submit"
                  disabled={nflStatus === 'submitting'}
                  className="bg-orange-500 hover:bg-orange-600 text-white shrink-0"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  {nflStatus === 'submitting' ? 'Signing up...' : 'Notify Me'}
                </Button>
              </form>
            )}
            {nflStatus === 'error' && (
              <p className="text-red-400 text-sm mt-2">Something went wrong. Try again or email support@sabotagefantasy.com</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* MLB Section */}
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-3 px-1">
          <Badge variant="outline" className="text-xs font-bold px-2 py-0.5">MLB</Badge>
          <span className="text-sm text-muted-foreground">2026 Season Live Now</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card
            className="cursor-pointer border-primary/30 hover:border-primary transition-colors bg-primary/5"
            onClick={() => setView('create')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Plus className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">Create a League</CardTitle>
                  <CardDescription>Start as commissioner</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground">
                Set up scoring rules, draft settings, sabotage mechanics, and invite your friends to compete.
              </p>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => setView('join')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <Users className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <CardTitle className="text-lg">Join a League</CardTitle>
                  <CardDescription>Have an invite code?</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground">
                Enter the invite code shared by your commissioner to join an existing league.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-2xl mx-auto">
        <h2 className="text-lg font-semibold font-heading text-foreground mb-4 text-center">How It Works</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: Gavel, label: 'Draft', desc: 'Snake or auction draft the worst players' },
            { icon: BarChart3, label: 'Trash Points', desc: 'Strikeouts, errors & blown saves = points' },
            { icon: Bomb, label: 'Sabotage', desc: 'Spin the wheel to mess with opponents' },
            { icon: Trophy, label: 'Win', desc: 'Most trash points wins the league' },
          ].map((feature) => (
            <div key={feature.label} className="text-center p-3 rounded-lg bg-muted/30 border border-border">
              <feature.icon className="w-6 h-6 mx-auto mb-2 text-primary" />
              <p className="text-sm font-medium text-foreground">{feature.label}</p>
              <p className="text-xs text-muted-foreground mt-1">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default MLBBetaLanding;
